import type { PuzzleBoard, ReplayUpload, SharedReplay, SocialApiPort, SubmitResult } from '../ports/social';
import { getHttpServerUrl } from './server-url.js';
import { fetchJson, HttpError } from './http-errors.js';

/** Who's asking: the sign-in token (if any) and the device's profile id. */
export type SocialIdentity = () => { token: string | null; profileId: string };

/** Uploading a long replay can take a while on a slow connection. */
const UPLOAD_TIMEOUT_MS = 60_000;

/** Shared replays, and shared puzzles' boards. */
export class HttpSocialApi implements SocialApiPort {
  constructor(
    private readonly identity: SocialIdentity,
    private readonly baseUrl: string = getHttpServerUrl(),
    private readonly fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
  ) {}

  async uploadReplay(upload: ReplayUpload, playerName: string): Promise<string> {
    const { id } = await this.post<{ id: string }>('/api/replays', this.withUploader(upload, playerName));
    return id;
  }

  getReplay(id: string): Promise<SharedReplay | null> {
    return this.orNull(this.get<SharedReplay>(`/api/replays/${encodeURIComponent(id)}`));
  }

  getBoard(puzzleId: string): Promise<PuzzleBoard | null> {
    return this.orNull(this.get<PuzzleBoard>(`/api/puzzles/${encodeURIComponent(puzzleId)}/board`));
  }

  submitToBoard(upload: ReplayUpload, playerName: string): Promise<SubmitResult> {
    return this.post(`/api/puzzles/${encodeURIComponent(upload.puzzleId)}/board`, this.withUploader(upload, playerName));
  }

  private withUploader(replay: ReplayUpload, playerName: string) {
    return { replay, playerName, profileId: this.identity().profileId };
  }

  private headers(json: boolean): Record<string, string> {
    const { token, profileId } = this.identity();
    return {
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Profile-Id': profileId,
    };
  }

  private get<T>(path: string): Promise<T> {
    return fetchJson<T>(this.fetchImpl, `${this.baseUrl}${path}`, { headers: this.headers(false) });
  }

  private post<T>(path: string, body: unknown): Promise<T> {
    return fetchJson<T>(this.fetchImpl, `${this.baseUrl}${path}`, {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify(body),
    }, UPLOAD_TIMEOUT_MS);
  }

  private async orNull<T>(request: Promise<T>): Promise<T | null> {
    try {
      return await request;
    } catch (err) {
      if (err instanceof HttpError && err.status === 404) return null;
      throw err;
    }
  }
}
